import { Component } from '@theme/component';

/**
 * @typedef {Object} TitleBannerRefs
 * @property {HTMLElement} layout
 * @property {HTMLButtonElement} toggleButton
 * @property {HTMLElement} moreLabel
 * @property {HTMLElement} lessLabel
 */

/** @extends {Component<TitleBannerRefs>} */
class TitleBannerComponent extends Component {
  requiredRefs = ['layout', 'toggleButton', 'moreLabel', 'lessLabel'];

  /** @type {HTMLElement | undefined} */
  #description;
  /** @type {AbortController | undefined} */
  #controller;
  /** @type {ResizeObserver | undefined} */
  #observer;
  /** @type {Animation | undefined} */
  #animation;
  #expanded = false;
  #previewHeight = 96;
  #lastWidth = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#initialize();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#cleanup();
    this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #initialize() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const blocks = [...this.refs.layout.children].filter(
      (element) => element instanceof HTMLElement && element.matches('.text-block, .group-block')
    );
    const title = blocks.find((element) => element.querySelector('h1'));
    const description = blocks.find((element) => element !== title && element.textContent?.trim());

    title?.classList.add('es-title-banner__title');
    this.dataset.hasDescription = description ? 'true' : 'false';
    this.#expanded = false;
    this.#syncState(false);

    if (!(description instanceof HTMLElement)) {
      this.refs.toggleButton.hidden = true;
      return;
    }

    this.#description = description;
    description.classList.add('es-title-banner__description');
    if (!description.id) description.id = `${this.closest('.shopify-section')?.id}-banner-description`;
    this.refs.toggleButton.setAttribute('aria-controls', description.id);
    this.dataset.enhanced = '';
    this.#refresh();
    this.addEventListener('focusin', this.#showFocusedContent, { signal });

    this.#lastWidth = this.getBoundingClientRect().width;
    this.#observer = new ResizeObserver(([entry]) => {
      if (!entry || Math.abs(entry.contentRect.width - this.#lastWidth) < 0.5) return;
      this.#lastWidth = entry.contentRect.width;
      this.#refresh();
    });
    this.#observer.observe(this);
    document.fonts.ready.then(() => {
      if (!signal.aborted) this.#refresh();
    });
  }

  #cleanup() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#animation?.cancel();
    this.#animation = undefined;
    this.#description?.style.removeProperty('height');
    this.#description?.classList.remove('es-title-banner__description');
    this.#description = undefined;
  }

  #refresh() {
    const description = this.#description;
    if (!description) return;

    this.#animation?.cancel();
    this.#animation = undefined;
    this.#previewHeight = parseFloat(getComputedStyle(this).getPropertyValue('--title-banner-preview')) || 96;
    description.style.height = 'auto';
    const overflows = description.scrollHeight > this.#previewHeight + 1;
    this.refs.toggleButton.hidden = !overflows;
    if (!overflows) this.#expanded = false;
    description.style.height = overflows && !this.#expanded ? `${this.#previewHeight}px` : 'auto';
    this.#syncState(overflows);
  }

  #syncState(overflows = !this.refs.toggleButton.hidden) {
    this.dataset.expanded = String(this.#expanded);
    this.dataset.clipped = String(overflows && !this.#expanded);
    this.refs.toggleButton.setAttribute('aria-expanded', String(this.#expanded));
    this.refs.moreLabel.hidden = this.#expanded;
    this.refs.lessLabel.hidden = !this.#expanded;
  }

  /** @param {FocusEvent} event */
  #showFocusedContent = (event) => {
    if (!this.#expanded && !this.refs.toggleButton.hidden && event.target instanceof Node && this.#description?.contains(event.target)) {
      this.#setExpanded(true);
    }
  };

  /** @param {boolean} expanded */
  #setExpanded(expanded) {
    const description = this.#description;
    if (!description) return;

    const startHeight = description.getBoundingClientRect().height;
    this.#animation?.cancel();
    this.#expanded = expanded;
    description.style.height = 'auto';
    const targetHeight = expanded ? description.scrollHeight : this.#previewHeight;
    this.#syncState();
    description.style.height = `${targetHeight}px`;

    if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (expanded) description.style.height = 'auto';
      return;
    }

    const styles = getComputedStyle(this);
    this.#animation = description.animate(
      { height: [`${startHeight}px`, `${targetHeight}px`] },
      {
        duration: (parseFloat(styles.getPropertyValue('--animation-speed-slow')) || 0.2) * 1000,
        easing: styles.getPropertyValue('--animation-easing').trim() || 'ease-in-out',
      }
    );
    this.#animation.onfinish = () => {
      description.style.height = this.#expanded ? 'auto' : `${this.#previewHeight}px`;
      this.#animation = undefined;
    };
  }

  /** @param {MouseEvent} event */
  toggle(event) {
    event.preventDefault();
    this.#setExpanded(!this.#expanded);
  }
}

if (!customElements.get('title-banner-component')) {
  customElements.define('title-banner-component', TitleBannerComponent);
}
